import React from "react"
import { Box, Chip, FormHelperText, Typography } from "@mui/material"
import { NumberFieldSeeker, ButtonLoadingSeeker } from "@ui-seeker/core"
import { dsColorNew } from "@palette"
import {
	rangeAddRowSx, rangeChipArrowSx, rangeChipFiguresSx, rangeChipSx, rangeChipUnitSx, rangeWellSx,
} from "Pages/DsRepricing/Page/@components/rangeSx"
import { batch, useSignal } from "@preact/signals-react"

/**
 * Shared "list of ranges" pattern used by Lead-Time, Duration and any
 * future numeric range list in the wizard.
 *
 * Layout (uniform across every consumer):
 *   ┌ Existing chips ─────────────────────────────────┐
 *   │  [ 3 → 7 ×] [ 14 → 30 ×]                        │
 *   ├─────────────────────────────────────────────────┤
 *   │  [ from ]  →  [ to ]        [ + Add … ]        │
 *   └─────────────────────────────────────────────────┘
 *
 * Existing entries are read-only chips (edit == delete + re-add, which
 * is simpler than an inline editor and keeps the visual weight low).
 * The input row is always at the bottom, always the same shape.
 *
 * Consumer responsibilities: field names, chip label, add-label,
 * placeholders, per-input validator, per-range validator (for the
 * cross-range overlap check), and the empty-state error copy.
 *
 * Everything else — layout, alignment, add/remove wiring, keyboard
 * focus after add — is owned here.
 */
// A preset chip reads as an offer, not as a value already chosen: outlined
// and quiet next to the solid blue chips of the ranges actually in the list.
const presetSx = (isLadder) => ({
	height: 24, fontSize: 12, fontWeight: 600,
	fontFamily: isLadder ? 'inherit' : 'inherit',
	bgcolor: dsColorNew.white,
	color: isLadder ? dsColorNew.appBlue : dsColorNew.textMuted,
	border: '1px dashed',
	borderColor: isLadder ? dsColorNew.appBlue : dsColorNew.grayBorder,
	'&:hover': { bgcolor: dsColorNew.pillActiveBg, borderColor: dsColorNew.appBlue },
	'& .MuiChip-label': { px: 0.9 },
})

export default function RangeList({
	values,
	onChange,
	fromKey, toKey,
	fromPlaceholder = 'from', toPlaceholder = 'to',
	addLabel = 'Add range',
	unitSuffix = null,                   // rendered inline next to inputs, e.g. "days"
	minValue = 0, maxValue = Infinity,
	validate,                            // optional (from, to) => 'error' | null (for cross-field rules like `to >= from`)
	overlapsExisting,                    // optional (existing, from, to) => boolean
	overlapError = 'Overlaps with an existing range',
	emptyError = 'Add at least one range',
	showEmptyError = true,
	autofocusAfterAdd = true,
	// One click instead of six keystrokes and a mouse trip, for the
	// handful of windows every rule uses anyway.
	//   ladders — a complete partition; offered only on an empty list,
	//             because replacing what someone just typed is rude and
	//             appending a partition would overlap everything.
	//   singles — one window each, appended; a chip that would overlap
	//             what is already there is disabled, not hidden, so the
	//             set of options does not shuffle as you click.
	// Both are `[from, to]` pairs in the same unit as the inputs.
	presets = null,
}) {
	const fromInput = useSignal('')
	const toInput = useSignal('')
	const err = useSignal(null)          // { from?, to? } | 'overlap' | null

	const parseNum = raw => (raw === '' || raw == null ? null : Number(raw))
	const isValidNum = v => Number.isFinite(v) && v >= minValue && v <= maxValue

	function inputErrors() {
		const f = parseNum(fromInput.value)
		const t = parseNum(toInput.value)
		const rangeErr = { from: null, to: null }
		if (fromInput.value !== '' && !isValidNum(f)) rangeErr.from = `${minValue}–${maxValue}`
		if (toInput.value   !== '' && !isValidNum(t)) rangeErr.to   = `${minValue}–${maxValue}`
		if (fromInput.value !== '' && toInput.value !== '' && f != null && t != null && t < f) {
			rangeErr.to = 'Must be ≥ from'
		}
		if (validate && f != null && t != null && !rangeErr.from && !rangeErr.to) {
			const custom = validate(f, t)
			if (custom) rangeErr.to = custom
		}
		return rangeErr
	}

	const rowErr = inputErrors()
	const isReadyToAdd = fromInput.value !== '' && toInput.value !== '' &&
	                     !rowErr.from && !rowErr.to

	function add() {
		if (!isReadyToAdd) return
		const from = Number(fromInput.value)
		const to   = Number(toInput.value)
		if (overlapsExisting && overlapsExisting(values, from, to)) {
			err.value = 'overlap'
			return
		}
		batch(() => {
			err.value = null
			const next = [...values, { [fromKey]: from, [toKey]: to }]
			                .sort((a, b) => a[fromKey] - b[fromKey])
			onChange(next)
			fromInput.value = ''
			toInput.value = ''
		})
	}

	function removeAt(i) {
		onChange(values.filter((_, idx) => idx !== i))
	}

	const isEmpty = values.length === 0

	function applyLadder(ranges) {
		onChange(ranges
			.map(([from, to]) => ({ [fromKey]: from, [toKey]: to }))
			.sort((a, b) => a[fromKey] - b[fromKey]))
	}

	function addPreset(from, to) {
		if (overlapsExisting && overlapsExisting(values, from, to)) return
		onChange([...values, { [fromKey]: from, [toKey]: to }]
			.sort((a, b) => a[fromKey] - b[fromKey]))
	}

	const singles = presets?.singles || []
	const ladders = presets?.ladders || []
	const showLadders = isEmpty && ladders.length > 0
	const showSingles = singles.length > 0

	return (
		<Box sx={rangeWellSx}>
			{(showLadders || showSingles) && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75,
				           alignItems: 'center' }}>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
					                  fontWeight: 600, mr: 0.25 }}>
						{showLadders ? 'Start with' : 'Add'}
					</Typography>
					{showLadders && ladders.map(l => (
						<Chip key={l.label} size="small" clickable
						      onClick={() => applyLadder(l.ranges)}
						      label={`${l.label} · ${l.ranges.length}`}
						      title={l.ranges.map(([a, b]) => `${a}–${b}`).join(', ')}
						      sx={presetSx(true)}/>
					))}
					{showSingles && singles.map(([from, to]) => {
						const taken = overlapsExisting
							? overlapsExisting(values, from, to) : false
						return (
							<Chip key={`${from}-${to}`} size="small"
							      clickable={!taken}
							      disabled={taken}
							      onClick={() => addPreset(from, to)}
							      label={`${from}–${to}`}
							      title={taken
							          ? 'Overlaps a range already in the list'
							          : `Add ${from}–${to}${unitSuffix ? ' ' + unitSuffix : ''}`}
							      sx={presetSx(false)}/>
						)
					})}
				</Box>
			)}

			{!isEmpty && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
					{values.map((item, i) => (
						<Chip key={`${item[fromKey]}-${item[toKey]}-${i}`}
						      label={
						          <Box component="span" sx={rangeChipFiguresSx}>
						              {item[fromKey]}
						              <Box component="span" sx={rangeChipArrowSx}>→</Box>
						              {item[toKey]}
						              {unitSuffix && <Box component="span" sx={rangeChipUnitSx}>
						                  {unitSuffix}
						              </Box>}
						          </Box>
						      }
						      onDelete={() => removeAt(i)}
						      sx={rangeChipSx}/>
					))}
				</Box>
			)}

			<Box sx={{
				display: 'flex', gap: 1, alignItems: 'center',
				...rangeAddRowSx(isEmpty),
			}}>
				<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
					<NumberFieldSeeker
						placeholder={fromPlaceholder}
						value={fromInput.value}
						onChange={e => { fromInput.value = e.target.value; err.value = null }}
						onKeyDown={e => { if (e.key === 'Enter') add() }}
						error={fromInput.value !== '' ? rowErr.from : undefined}
						sx={{ flex: 1 }}/>
					<Box component="span" sx={{ fontSize: 14, fontWeight: 600,
					                            color: dsColorNew.textSubtle, flex: '0 0 auto' }}>→</Box>
					<NumberFieldSeeker
						placeholder={toPlaceholder}
						value={toInput.value}
						onChange={e => { toInput.value = e.target.value; err.value = null }}
						onKeyDown={e => { if (e.key === 'Enter') add() }}
						error={toInput.value !== '' ? rowErr.to : undefined}
						sx={{ flex: 1 }}/>
					{unitSuffix && (
						<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
						                  fontWeight: 500, flex: '0 0 auto' }}>
							{unitSuffix}
						</Typography>
					)}
				</Box>
				<ButtonLoadingSeeker
					variant={isReadyToAdd ? 'contained' : 'outlined'}
					onClick={add}
					disabled={!isReadyToAdd}
					sx={{
						flex: '0 0 auto', textTransform: 'none',
						fontWeight: 500, fontSize: 13,
						height: 40, borderRadius: '8px',
						...(isReadyToAdd ? {
							bgcolor: dsColorNew.appBlue,
							color: dsColorNew.white,
							boxShadow: 'none',
							'&:hover': { bgcolor: dsColorNew.appBlueHover, boxShadow: 'none' },
						} : {
							borderStyle: 'dashed',
							color: dsColorNew.textSubtle,
							borderColor: dsColorNew.grayBorder,
						}),
					}}>
					+ {addLabel}
				</ButtonLoadingSeeker>
			</Box>

			{err.value === 'overlap' && (
				<FormHelperText error sx={{ mx: 0 }}>{overlapError}</FormHelperText>
			)}
			{showEmptyError && isEmpty && (
				<FormHelperText error sx={{ mx: 0 }}>{emptyError}</FormHelperText>
			)}
		</Box>
	)
}
